import { useDropzone } from 'react-dropzone';
import { Box, Input, Progress } from '@apia/theme';
import Config from 'config';
import React from 'react';
import { TModify } from '../../../../../types';
import { TUploaderFileInfo } from '../../../../common/uploader/types';
import { IApiaUploaderList } from './ApiaUploaderList';
import DebugInfo from '../../../../common/DebugInfo';
import { toBoolean } from '../../../../../utils/util';
import FileElement from './FileElement';
import { isOneClickUploadEnabled } from '../../../../common/uploader/UploaderApi';
import useContext from '../../../../../hooks/useContext';
import UploaderContext from '../../../../common/uploader/context';

const isAndroid = () =>
  typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

export type TUploaderListElement = TModify<
  Omit<IApiaUploaderList, 'uploadedList'>,
  { id: string; progress?: number }
> & {
  autoFocus?: boolean;
  file: TUploaderFileInfo;
};

const UploaderListElement = (props: TUploaderListElement) => {
  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    disabled:
      (props.progress !== undefined && props.progress < 100) ||
      Number(props.file.docId) < 0 ||
      toBoolean(props.file.isVirtual),
    onDropAccepted: React.useCallback(
      (files: File[]) => {
        if (props.onDrop) props.onDrop(props.file, files[0]);
      },
      [props],
    ),
    noClick: true,
  });

  const { api } = useContext(UploaderContext);
  const shouldShowDragMessage =
    isDragActive || (props.progress !== undefined && props.progress < 100);
  const canVersion =
    props.file.canEdit &&
    Number(props.file.docId) >= 0 &&
    !props.noVersion &&
    (!isOneClickUploadEnabled(api.configurationObject.oneClickUpload) ||
      props.onOneClickUpload);

  return (
    <Box
      className="uploader-listItem"
      sx={React.useMemo(
        () => ({
          flexDirection: props.forGrid ? 'row' : 'column',
          pl: props.forGrid ? 2 : 0,
        }),
        [props.forGrid],
      )}
      {...(canVersion ? getRootProps() : null)}
      data-filename={props.file.docName}
    >
      <DebugInfo id="a" data={props} />
      {canVersion && (
        <Input
          sx={{ display: 'none' }}
          {...getInputProps()}
          {...(isAndroid() ? { capture: 'environment' } : {})}
        />
      )}
      {canVersion && shouldShowDragMessage && (
        <Box tabIndex={0} className="dropzone">
          {props.progress !== undefined && props.progress < 100 && (
            <Progress max={100} value={props.progress} />
          )}
          {(props.progress === undefined || props.progress === 100) &&
            (isDragActive
              ? Config.DOC_UPLOAD_DROP_MESSAGE
              : Config.DOC_UPLOAD_DRAG_MESSAGE)}
          <Input
            sx={{ display: 'none' }}
            {...getInputProps()}
            {...(isAndroid() ? { capture: 'environment' } : {})}
          />
        </Box>
      )}
      <Box
        sx={React.useMemo(
          () => ({ opacity: shouldShowDragMessage ? 0 : 1 }),
          [shouldShowDragMessage],
        )}
      >
        <FileElement {...props} />
      </Box>
    </Box>
  );
};

export default UploaderListElement;
